import { boxTruckVehicleLabel, CanonicalLoad, sylectusStopLabel } from '@numeo/types-core'
import { displayNumeoLoadId, loadBrokerEmail, loadBrokerPhone } from '@numeo/types'
import { formatPickupDate } from '@/utils/format-date'
import { cityLabel, fmtDateOrText } from './format'
import { formatMilesOut, ResolvedMilesOut } from './roadDistance'

/** Field accessors that fold the SYLECTUS_EMAIL details and the shared CanonicalLoad columns. */

const SOURCE_GROUP_LABELS: Record<string, string> = {
    SYLECTUS: 'Sylectus',
    DAT: 'DAT',
    TRUCKSTOP: 'Truckstop',
    ARRIVE: 'Arrive',
    ECHO: 'Echo',
    BROKER_PORTAL: 'Numeo Loads',
    NAVISPHERE: 'C.H. Robinson',
    BEON: 'Nolan',
}

/**
 * Source-group key for the loadboard's Sources bar — the unit the chips filter and count by. The board unions
 * SYLECTUS_EMAIL with live DAT; each row keeps its TRUE source in `sourceType` (SYLECTUS_EMAIL, DAT,
 * TRUCKSTOP, ARRIVE, …), so we group by that. Variants collapse to one chip (SYLECTUS_EMAIL → SYLECTUS; the
 * two TruckStop feeds → TRUCKSTOP; the two DAT feeds → DAT). Keys align with the SOURCE_ICONS / sourceIconKey
 * convention so a chip's key is also its icon key. */
const sourceGroupKeyOf = (sourceType: string | null | undefined): string => {
    switch (sourceType) {
        case 'SYLECTUS_EMAIL':
            return 'SYLECTUS'
        case 'TRUCKSTOP':
        case 'TRUCKSTOP_ACCOUNT':
            return 'TRUCKSTOP'
        case 'DAT':
        case 'DAT_EXTENSION':
            return 'DAT'
        default:
            return sourceType ?? ''
    }
}

export const sourceGroupKey = (load: CanonicalLoad): string => sourceGroupKeyOf(load.sourceType)

/** Sources the box-truck board never surfaces as a chip (nor counts) — Telegram is out of scope here. A load
 *  whose group is excluded is dropped from the board entirely (no orphan rows with no chip to filter them). */
export const EXCLUDED_SOURCE_GROUPS: ReadonlySet<string> = new Set(['TELEGRAM'])

/** Human label for a source-group key (chip text). Falls back to Title Case for any unmapped source. */
export const sourceGroupLabel = (key: string): string =>
    SOURCE_GROUP_LABELS[key] ??
    key
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())

/** Short source label for a load — its Sources-bar group name (Sylectus, DAT, Truckstop, …). The row's icon
 *  shows the same source via sourceIconKey; this is the text fallback when a source has no icon. */
export const sourceLabel = (load: CanonicalLoad): string => sourceGroupLabel(sourceGroupKey(load))

/** The load-hub SOURCE_ICONS key for a box-truck load's source. SYLECTUS_EMAIL shares the Sylectus logo
 *  (SOURCE_ICONS has no dedicated SYLECTUS_EMAIL entry); DAT maps straight through. */
export const sourceIconKey = (load: CanonicalLoad): string => (load.sourceType === 'SYLECTUS_EMAIL' ? 'SYLECTUS' : (load.sourceType ?? ''))

/** Stable row/dedupe key — externalId (matches what search returns after prefix-strip) or the DB id. */
export const loadKey = (load: CanonicalLoad): string => load.externalId || String(load.id)

/** The Sylectus order number for the ORDER ID column — SYLECTUS_EMAIL only. Other sources (DAT, BEON, …) have
 *  no order-id concept, so this returns null and the column is left blank rather than showing an inconsistent
 *  postersReferenceId (which is a reference id, surfaced in the load drawer instead). */
export const orderId = (load: CanonicalLoad): string | null =>
    load.sourceType === 'SYLECTUS_EMAIL' ? load.postersReferenceId || load.details?.sylectusRefNo || null : null

/** Numeo-owned friendly load id (5-digit handle). Stored at ingest for SYLECTUS_EMAIL; DAT box-truck loads
 *  don't carry one, so derive it with the SAME deterministic hash — a uniform Numeo id for every load, no
 *  stored field, no migration. Ingest hashes the COMPOSITE externalId (`SOURCE|rawId`) but the search strips
 *  that prefix, so re-qualify with sourceType to hash the exact input ingest used. */
export const numeoId = (load: CanonicalLoad): string | null => displayNumeoLoadId(load.details?.numeoId, load.sourceType, load.externalId)

/** Clean, deduplicated vehicle label — the shared box-truck labeller (Sylectus raw size → box-truck
 *  vocabulary, DAT → canonical). Drives the row/drawer display and the client-side equipment filter. */
export const vehicleLabel = (load: CanonicalLoad): string => boxTruckVehicleLabel(load.details?.sylectusVehicleSize, load.equipmentType)

/** The person at the brokerage, as the source named them ("Mario Martinez") — distinct from
 *  `brokerCompanyName`, which is the brokerage itself. Sylectus's posted-load email carries it on the
 *  "Broker Name:" line and the ingest stores it; DAT and the Sylectus API sweep have no such field, so a
 *  board mixing sources will have it on some rows and not others and every caller must handle null.
 *  Trimmed because a legacy row can hold whitespace, which would render as an empty line rather than
 *  being skipped. No poster fallback: CanonicalLoad has no poster contact NAME, only email and phone. */
export const brokerName = (load: CanonicalLoad): string | null => load.brokerContactName?.trim() || null

export const brokerEmail = (load: CanonicalLoad): string | null => loadBrokerEmail(load)

export const brokerPhone = (load: CanonicalLoad): string | null => loadBrokerPhone(load)

/** Best pickup datetime: the parsed window start, else the verbatim email text ("ASAP", etc.). The
 *  free-text lives in details (persisted to PG); pickupDateTime is a transient (non-persisted) fallback. */
const pickupAt = (load: CanonicalLoad): Date | string | null => load.pickupWindowStart ?? load.details?.sylectusPickupText ?? load.pickupDateTime ?? null

const deliveryAt = (load: CanonicalLoad): Date | string | null =>
    load.deliveryWindowStart ?? load.details?.sylectusDeliveryText ?? load.deliveryDateTime ?? null

/** SYLECTUS posts carry a real ET wall-clock instant / free text ("ASAP"); every other upstream (DAT etc.)
 *  sends the pickup as a UTC-midnight date-only window. Both Sylectus source variants (live API + email feed)
 *  count — the single check the board reuses (date labels here, exact-time posted-age in BoxTruckTable). */
const isSylectusSource = (load: CanonicalLoad): boolean => load.sourceType === 'SYLECTUS' || load.sourceType === 'SYLECTUS_EMAIL'

/**
 * Display label for the pickup/delivery date.
 *
 * A Sylectus post states its stop time in the POSTER'S zone, and the board carries EST, CEN and PAC posts
 * side by side — so it shows the source's own line verbatim ({@link sylectusStopLabel}). Rendering the parsed
 * instant in the viewer's zone rewrote all of them into one clock the broker never quoted: the email said
 * "11:00 EST", the board said "09:00 PM", and operators could not tell the two were the same moment. The
 * hover ({@link stopLocalTimeTitle}) still carries their local equivalent.
 *
 * DAT (and other upstream) window dates arrive as UTC midnight (date-only), so a naive
 * `new Date(...).toLocaleString()` in a negative-offset zone renders the day BEFORE with a bogus time (DAT
 * "Aug 14" was showing "08/13/2026, 08:00 PM"). Render the calendar date via the shared `formatPickupDate`
 * with `parseAs:'utc'` — exactly what load-hub does. Sylectus rows with no source text (ingested before it
 * was persisted) keep the datetime-or-verbatim form, since their window is a real instant, not a date-only value.
 */
export const pickupLabel = (load: CanonicalLoad): string =>
    sylectusStopLabel(load.details?.sylectusPickupText) ??
    (isSylectusSource(load)
        ? fmtDateOrText(pickupAt(load))
        : formatPickupDate(load.pickupWindowStart, load.pickupWindowEnd, { format: 'MM/DD/YYYY', parseAs: 'utc', onInvalid: 'raw' }) ||
          fmtDateOrText(pickupAt(load)))

export const deliveryLabel = (load: CanonicalLoad): string =>
    sylectusStopLabel(load.details?.sylectusDeliveryText) ??
    (isSylectusSource(load)
        ? fmtDateOrText(deliveryAt(load))
        : formatPickupDate(load.deliveryWindowStart, load.deliveryWindowEnd, { format: 'MM/DD/YYYY', parseAs: 'utc', onInvalid: 'raw' }) ||
          fmtDateOrText(deliveryAt(load)))

/** The same instant in the VIEWER's zone — the drawer's hover, so an operator can still reconcile a
 *  source-zone stop time ("11:00 EST") with the pickup DATE FILTER, which is evaluated on the absolute
 *  instant against their own local day. Undefined when there is no parsed instant (an "ASAP" post) or no
 *  source text to disambiguate, so the attribute simply isn't rendered. */
export const stopLocalTimeTitle = (verbatim: string | null | undefined, instant: CanonicalLoad['pickupWindowStart']): string | undefined =>
    sylectusStopLabel(verbatim) && instant ? `Your time: ${fmtDateOrText(instant)}` : undefined

/** "City, ST 60136" — the stop locality with its ZIP when present. */
export const localityWithZip = (city: string | null | undefined, state: string | null | undefined, zip: string | null | undefined): string => {
    const base = cityLabel(city, state)
    return zip && base !== '—' ? `${base} ${zip}`.trim() : base
}

/**
 * A clean, shareable plaintext summary of a load for the clipboard — the "Copy Load" action. Groups the
 * route + schedule, the specs, and the notes with blank lines between them; omits spec/notes lines that have
 * no value so the block stays tidy. Dates use the same verbatim-or-formatted rule as the drawer display.
 */
/** Omitting `milesOut` drops the line entirely (no driver picked); `null` prints it as unknown. Callers pass
 *  the whole resolved value, but the ≈ marker is deliberately ignored — copied text goes to brokers. */
type LoadClipboardOptions = Partial<ResolvedMilesOut>

export const buildLoadClipboardText = (load: CanonicalLoad, { milesOut }: LoadClipboardOptions = {}): string => {
    const lines: string[] = [
        `Pick-up at: ${localityWithZip(load.pickupCity, load.pickupStateProv, load.pickupPostalCode)}`,
        `Pick-up date: ${pickupLabel(load)}`,
        '',
        `Deliver to: ${localityWithZip(load.deliveryCity, load.deliveryStateProv, load.deliveryPostalCode)}`,
        `Delivery date: ${deliveryLabel(load)}`,
        '',
    ]
    if (load.distanceMiles != null) lines.push(`Miles: ${Math.round(load.distanceMiles)}`)
    if (milesOut !== undefined) {
        const label = formatMilesOut({ milesOut })
        lines.push(`Miles out: ${label ? `${label} out` : '-'}`)
    }
    if (load.pieceCount != null) lines.push(`Pieces: ${load.pieceCount}`)
    if (load.weightLbs != null) lines.push(`Weight: ${Math.round(load.weightLbs)}`)
    // SYLECTUS_EMAIL posts carry free-text dimensions ("48 x 40 x 40"); other sources have none — omitted.
    if (load.details?.sylectusDimensions?.trim()) lines.push(`Load Dimensions: ${load.details.sylectusDimensions.trim()}`)
    lines.push(`Suggested Truck Size: ${vehicleLabel(load)}`)
    if (load.comment?.trim()) lines.push('', `Notes: ${load.comment.trim()}`)
    return lines.join('\n')
}
